import { useState } from 'react';
import { supabase } from '../lib/supabase';
import type { NewsCategory } from '../types/news';
import slugify from 'slugify';

export function useNewsCategories() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('news_categories')
        .select('*')
        .eq('is_active', true)
        .order('position', { ascending: true });

      if (fetchError) throw fetchError;

      return data as NewsCategory[];
    } catch (err: any) {
      console.error('Error fetching categories:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async (category: Partial<NewsCategory>) => {
    try {
      setLoading(true);
      setError(null);

      if (!category.name) {
        throw new Error('Name is required');
      }

      const slug = slugify(category.name, { lower: true, strict: true });

      const { data: newCategory, error: insertError } = await supabase
        .from('news_categories')
        .insert([{ ...category, slug }])
        .select()
        .single();

      if (insertError) throw insertError;

      return newCategory;
    } catch (err: any) {
      console.error('Error creating category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateCategory = async (id: string, updates: Partial<NewsCategory>) => {
    try {
      setLoading(true);
      setError(null);

      if (updates.name) {
        updates.slug = slugify(updates.name, { lower: true, strict: true });
      }

      const { data: updatedCategory, error: updateError } = await supabase
        .from('news_categories')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (updateError) throw updateError;

      return updatedCategory;
    } catch (err: any) {
      console.error('Error updating category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteCategory = async (id: string) => {
    try {
      setLoading(true);
      setError(null);

      const { error: deleteError } = await supabase
        .from('news_categories')
        .update({ is_active: false })
        .eq('id', id);

      if (deleteError) throw deleteError;
    } catch (err: any) {
      console.error('Error deleting category:', err);
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory
  };
}